/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { pdfjs } from 'react-pdf';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { SizeMe } from 'react-sizeme';
import { mixins } from '@blend/consumer-components';
import { mediaBreakpoints } from '../../../common/responsive';
import { mobileDocSubheaderHeight } from '../Signing/DocumentViewer/MobileDocSubheader';
import { ReviewDocument } from '../../../types/documents';
import { Scrollable } from '../../../components/Scrollable/Scrollable';
import { useDocumentViewer } from '../Signing/DocumentViewer/hooks/useDocumentViewer';
import { DocumentWrapper } from './DocumentWrapper';

import './PdfViewer.scss';

// react-pdf relies on the following worker script to render PDFs.
// The script renders PDFs in a separate thread so page performance is not impacted.
// We need to manually pull this in, otherwise the request for the script 404s
// and PDFs cannot render.

// https://stackoverflow.com/a/69625304
pdfjs.GlobalWorkerOptions.workerSrc = '/assets/js/pdf.worker.min.v2.9.359.js';

export namespace MultiPdfViewer {
  export interface Props {
    documents: ReviewDocument[];
  }
}

const styles = StyleSheet.create({
  document: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    margins: mixins.margin(8),
  },
  page: {
    ...mixins.margin(8, 'auto'),
    [mediaBreakpoints.mobile]: {
      ...mixins.margin(16, 8),
    },
    ':last-child': {
      marginTop: 0,
    },
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  pdfViewer: {
    height: '100%',
    width: '100%',
    [mediaBreakpoints.mobile]: {
      marginTop: mobileDocSubheaderHeight + 8,
    },
  },
  documentNameWrapper: {
    textAlign: 'left',
    ...mixins.padding(8),
  },
  docWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    position: 'relative',
  },
});

export const MultiPdfViewer: React.FC<MultiPdfViewer.Props> = ({ documents }) => {
  const { onScroll } = useDocumentViewer(false);

  return (
    <SizeMe refreshMode={'debounce'}>
      {({ size }) => (
        <div className={css(styles.docWrapper)} data-it="DocumentViewer">
          <Scrollable onScroll={onScroll}>
            {documents && documents.length > 0 && (
              <div className={css(styles.pdfViewer)} data-it="pdf-viewer">
                <TransformWrapper wheel={{ wheelDisabled: true, step: 0.1 }} panning={{ disabled: true }}>
                  <TransformComponent
                    wrapperStyle={{ overflow: 'auto', width: '100%', height: '100%' }}
                    contentStyle={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {documents.map(doc => {
                      return (
                        <DocumentWrapper
                          key={doc.id}
                          fileName={doc.fileName}
                          pdfUrl={doc.pdfUrl}
                          width={Number(size.width)}
                        />
                      );
                    })}
                  </TransformComponent>
                </TransformWrapper>
              </div>
            )}
          </Scrollable>
        </div>
      )}
    </SizeMe>
  );
};
